import { useNavigation } from "@/providers";
import { RobotoTooltip } from "@/shared/components";
import { HighlightedSpan } from "@/shared/components/ai/AISummaryMarkdownLink/HighlightedSpan.tsx";
import {
  type DirectEntityType,
  ENTITY_TYPE_LABELS,
  robotoEntityPath,
} from "@/shared/components/RobotoMarkdown/entityRoutes";
import {
  HoverActionToolbar,
  hoverActions,
} from "@/shared/components/RobotoMarkdown/HoverActionToolbar";
import {
  useJumpToTimestampAction,
  useSetAnchorAction,
} from "@/shared/components/RobotoMarkdown/timeFormat";

interface EntityLinkProps {
  entityType: DirectEntityType;
  entityId: string;
  text: string;
  /** Epoch ns; only meaningful for types whose page seeks to a time. */
  time?: bigint;
  onLinkClick?: () => void;
}

/**
 * Link to an entity whose page is addressable by ID.
 *
 * Unlike its dataset/file/topic siblings this fetches nothing, so the link is
 * live on first paint. The cost is that a link to a deleted or cross-org
 * entity still renders; the destination page reports that, same as pasting
 * the ID into the address bar would.
 */
export const EntityLink = ({
  entityType,
  entityId,
  text,
  time,
  onLinkClick,
}: EntityLinkProps) => {
  const { goto } = useNavigation();
  const jumpAction = useJumpToTimestampAction(time);
  const setAnchorAction = useSetAnchorAction(time);
  const path = robotoEntityPath(entityType, entityId, time);

  if (path === undefined) {
    return <span>{text}</span>;
  }

  return (
    <HoverActionToolbar actions={hoverActions(jumpAction, setAnchorAction)}>
      <RobotoTooltip title={`${ENTITY_TYPE_LABELS[entityType]}: ${entityId}`}>
        <HighlightedSpan
          onClick={() => {
            onLinkClick?.();
            goto.navigateTo(path);
          }}
        >
          {text}
        </HighlightedSpan>
      </RobotoTooltip>
    </HoverActionToolbar>
  );
};
